import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  Add,
  Apps,
  Settings,
  Launch,
  ShoppingCart,
  Description,
  DateRange,
  Api
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getApplications, ApplicationAPIError } from '../api/application';
import { subscriptAPIV2, OwnerAPIError } from '../api/owner';
import { Application } from '../types/application';
import { ProductSetting } from '../types/owner';

export const ApplicationListPage: React.FC = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState<Application[]>([]);
  const [productSettings, setProductSettings] = useState<ProductSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getApplications();
      setApplications(data);
    } catch (err) {
      if (err instanceof ApplicationAPIError) {
        setError(err.message);
      } else {
        setError('アプリケーション一覧の読み込みに失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFromProduct = async () => {
    try {
      setLoadingProducts(true);
      const products = await subscriptAPIV2.getProductSettings();
      setProductSettings(products);
      setCreateDialogOpen(true);
    } catch (err) {
      if (err instanceof OwnerAPIError) {
        if (err.status === 404) {
          setError('オーナー設定が必要です。まず設定ページでサブスクライトAPIキーを設定してください。');
          navigate('/owner-settings');
        } else {
          setError(err.message);
        }
      } else {
        setError('商品設定の取得に失敗しました');
      }
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleProductSelect = (product: ProductSetting) => {
    setCreateDialogOpen(false);
    // 商品を選択してアプリケーション作成画面に遷移
    navigate('/applications/create-from-product', {
      state: {
        productSetting: product
      }
    });
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'suspended': return 'warning';
      default: return 'default';
    }
  };

  const getStatusText = (status: string): string => {
    switch (status) {
      case 'active': return 'アクティブ';
      case 'inactive': return '非アクティブ';
      case 'suspended': return '停止中';
      default: return status;
    }
  };

  const formatPrice = (price: number | null | undefined): string => {
    if (price === null || price === undefined) return '-';
    return `¥${price.toLocaleString()}`;
  };

  const formatPaymentType = (type: number): string => {
    // payment_type: 0 = 単発, 1 = 定額, 2 = 単発（Payment Link）
    return (type === 0 || type === 2) ? '単発' : '定額';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box maxWidth={1200} margin="auto" padding={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={3}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Apps />
          マイアプリケーション
        </Typography>

        <Button
          variant="outlined"
          startIcon={<Settings />}
          onClick={() => navigate('/owner-settings')}
        >
          オーナー設定
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ marginBottom: 3 }}>
          {error}
        </Alert>
      )}

      <Typography variant="body1" color="text.secondary" gutterBottom>
        あなたが管理しているアプリケーションの一覧です。
        サブスクライトの商品設定から新しいアプリケーションを作成できます。
      </Typography>

      {applications.length === 0 ? (
        <Card sx={{ textAlign: 'center', padding: 4, marginTop: 3 }}>
          <CardContent>
            <Apps sx={{ fontSize: 64, color: 'text.secondary', marginBottom: 2 }} />
            <Typography variant="h6" gutterBottom>
              アプリケーションがありません
            </Typography>
            <Typography variant="body2" color="text.secondary" marginBottom={3}>
              サブスクライトの商品設定から新しいアプリケーションを作成しましょう
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateFromProduct}
              disabled={loadingProducts}
            >
              {loadingProducts ? '商品を読み込み中...' : '商品から作成'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <Grid container spacing={3}>
            {applications.map((app) => (
              <Grid item xs={12} md={6} lg={4} key={app.application_id}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" marginBottom={2}>
                      <Typography variant="h6" noWrap>
                        {app.name}
                      </Typography>
                      <Chip
                        label={getStatusText(app.status)}
                        color={getStatusColor(app.status)}
                        size="small"
                      />
                    </Box>

                    <Typography variant="body2" color="text.secondary" marginBottom={2} sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}>
                      {app.description}
                    </Typography>

                    <Box marginBottom={2}>
                      <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                        <Api fontSize="small" />
                        バージョン: {app.version || '未設定'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                        <DateRange fontSize="small" />
                        作成: {app.created_at ? new Date(app.created_at).toLocaleDateString() : '不明'}
                      </Typography>
                      {app.product_setting_name && (
                        <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                          <ShoppingCart fontSize="small" />
                          商品: {app.product_setting_name}
                        </Typography>
                      )}
                    </Box>

                    <Button
                      variant="outlined"
                      startIcon={<Launch />}
                      fullWidth
                      onClick={() => navigate(`/applications/${app.application_id}`)}
                    >
                      管理
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* FAB for adding new application */}
          <Fab
            color="primary"
            sx={{ position: 'fixed', bottom: 24, right: 24 }}
            onClick={handleCreateFromProduct}
            disabled={loadingProducts}
          >
            {loadingProducts ? <CircularProgress size={24} /> : <Add />}
          </Fab>
        </>
      )}

      {/* 商品選択ダイアログ */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ShoppingCart />
            商品設定を選択
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" marginBottom={2}>
            サブスクライトで設定された商品から、アプリケーションを作成する商品を選択してください。
          </Typography>

          <List>
            {productSettings.map((product, index) => (
              <React.Fragment key={product.id}>
                <ListItemButton onClick={() => handleProductSelect(product)}>
                  <ListItemIcon>
                    <Description />
                  </ListItemIcon>
                  <ListItemText
                    primary={product.name}
                    secondary={
                      <Box>
                        <Typography variant="caption" display="block">
                          {formatPaymentType(product.payment_type)} -
                          {(product.payment_type === 0 || product.payment_type === 2)
                            ? ` ${formatPrice(product.single_price)}`
                            : ` ${formatPrice(product.cycle_price)} / ${product.cycle_interval_value}${product.cycle_interval_unit}`
                          }
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {product.description}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItemButton>
                {index < productSettings.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>

          {productSettings.length === 0 && (
            <Box textAlign="center" padding={3}>
              <Typography variant="body2" color="text.secondary">
                販売中の商品設定が見つかりません
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};